import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ArrowDownRight,
  ArrowUpRight,
  ChevronLeft,
  Copy,
  Eye,
  EyeOff,
  Mail,
  Pause,
  Phone,
  Store,
  Gamepad2,
  Percent,
  Pencil,
  User,
} from 'lucide-react';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import AppLayout from '../components/layout/AppLayout';
import { AgentPageLayout } from '../components/layout/AgentPageLayout';
import SellCreditsCashToShopTrigger from '../components/credits/SellCreditsCashToShopTrigger';
import WithdrawCreditsFromShopTrigger from '../components/credits/WithdrawCreditsFromShopTrigger';
import { useAuth } from '../hooks/useAuth';
import {
  fetchDashboard,
  fetchAgentShopWalletTransactions,
  fetchAgentShopWallets,
  fetchAgentShopOnlineBalanceLedger,
  fetchAgentShopCreditTransactionById,
  fetchAgentShopUsdLedgerEntryById,
  fetchPublicGameProviders,
  fetchShop,
  fetchShopGameMappings,
  putShopGameMappings,
  suspendShop,
  updateShopCost,
  updateShopName,
  updateShopPassword,
  updateShopPopupPermission,
} from '../services/api';
import type { AgentDashboardData, AgentShop, AgentShopWalletSummary, ShopWalletLedgerRow } from '../types';
import type { ShopUsdWalletLedgerRow } from '../types';
import {
  formatSigned,
  getPresentation,
  txCreatedAt,
} from '../utils/shopWalletLedger';
import {
  CreditTransactionDetailModal,
  NumberedPagination,
  UsdWalletTransactionDetailModal,
  ViewTransactionButton,
  usePortalLedgerDetailState,
  useTimezone,
  PAGE,
  accountStatus,
  ledgerStatus,
  PORTAL_TERMS,
  usdLedgerTypeLabel,
} from '@kioskgaming/ui';
import { formatCostRate, percentInputToRate } from '../utils/costFormat';

const MIN_SHOP_PASSWORD_LEN = 6;
type ShopDetailTab = 'overview' | 'security' | 'credit_transactions' | 'usd_transactions' | 'games';

function usdTxCreatedAt(r: ShopUsdWalletLedgerRow) {
  const raw = r?.createdAt ?? r?.created_at;
  return raw ? String(raw).slice(0, 19) : '—';
}

function signedNetAmount(r: ShopUsdWalletLedgerRow) {
  const t = String((r as any)?.type ?? '').toLowerCase();
  const gross = Number((r as any)?.amountGross ?? (r as any)?.amount_gross ?? NaN);
  const net = Number((r as any)?.amountNet ?? (r as any)?.amount_net ?? 0);
  const amt =
    t === 'withdrawal' && Number.isFinite(gross) && gross > 0 ? gross : net;
  if (t === 'withdrawal') return -Math.abs(amt);
  return amt;
}

export default function ShopDetailPageRedesign() {
  const { timezone } = useTimezone();
  const { user } = useAuth();
  const { shopId } = useParams<{ shopId: string }>();
  const [shop, setShop] = useState<AgentShop | null>(null);
  const [dash, setDash] = useState<AgentDashboardData | null>(null);
  const [wallets, setWallets] = useState<AgentShopWalletSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [costPctStr, setCostPctStr] = useState('');
  const [costBusy, setCostBusy] = useState(false);
  const [transferTxs, setTransferTxs] = useState<ShopWalletLedgerRow[]>([]);
  const [transferTxLoading, setTransferTxLoading] = useState(false);
  const [transferTxPage, setTransferTxPage] = useState(1);
  const [transferTxTotalPages, setTransferTxTotalPages] = useState(0);
  const [transferTxTotal, setTransferTxTotal] = useState(0);
  const [transferTxNonce, setTransferTxNonce] = useState(0);
  const transferTxLimit = 15;
  const transferPrevShopIdRef = useRef<string | undefined>(undefined);

  const [usdTxs, setUsdTxs] = useState<ShopUsdWalletLedgerRow[]>([]);
  const [usdTxLoading, setUsdTxLoading] = useState(false);
  const [usdTxPage, setUsdTxPage] = useState(1);
  const [usdTxTotalPages, setUsdTxTotalPages] = useState(0);
  const [usdTxTotal, setUsdTxTotal] = useState(0);
  const [usdTxNonce] = useState(0);
  const usdTxLimit = 15;
  const usdPrevShopIdRef = useRef<string | undefined>(undefined);
  const [newShopPassword, setNewShopPassword] = useState('');
  const [confirmShopPassword, setConfirmShopPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [activeTab, setActiveTab] = useState<ShopDetailTab>('overview');
  const [copiedShopId, setCopiedShopId] = useState(false);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [gameMode, setGameMode] = useState<'all' | 'selected'>('all');
  const [selectedGameIds, setSelectedGameIds] = useState<string[]>([]);
  const [agentPoolIds, setAgentPoolIds] = useState<string[]>([]);
  const [gameProviders, setGameProviders] = useState<Array<{ id: string; name: string; code: string }>>([]);
  const [savingGames, setSavingGames] = useState(false);
  const [costModalOpen, setCostModalOpen] = useState(false);
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameBusy, setNameBusy] = useState(false);

  const minShopCostExclusive = useMemo(() => {
    const fromShop = shop?.minShopCostRateExclusive;
    if (fromShop != null && Number.isFinite(Number(fromShop))) {
      const n = Number(fromShop);
      return Math.min(1, Math.max(0, n));
    }
    const u = user?.costRate;
    if (u != null && Number.isFinite(u)) return Math.min(1, Math.max(0, u));
    return 0;
  }, [shop?.minShopCostRateExclusive, user?.costRate]);

  const minShopCostPercent = Math.min(100, Math.max(0, minShopCostExclusive * 100));
  const currentCostPercent = Number.isFinite(Number(costPctStr)) ? Number(costPctStr) : 0;
  const normalizedCostPercent = Math.max(0, Math.min(100, currentCostPercent));
  const costProgressColor = normalizedCostPercent >= minShopCostPercent ? '#E8551F' : '#DC2626';

  const savedCostPercent = shop
    ? Math.min(100, Math.max(0, Number(shop.costRate ?? 0) * 100))
    : 0;
  const savedBarColor = savedCostPercent >= minShopCostPercent ? '#E8551F' : '#DC2626';

  const loadShop = useCallback(async () => {
    if (!shopId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    try {
      const [shopRes, dashRes, walletRes] = await Promise.all([
        fetchShop(shopId),
        fetchDashboard(),
        fetchAgentShopWallets(shopId),
      ]);
      if (shopRes.success && shopRes.data) {
        setShop(shopRes.data);
        if (dashRes.success && dashRes.data) setDash(dashRes.data);
        if (walletRes.success && walletRes.data) setWallets(walletRes.data);
        else setWallets(null);
      } else {
        setShop(null);
        setNotFound(true);
        setWallets(null);
        if (shopRes.message) toast.error(shopRes.message);
      }
    } catch {
      setShop(null);
      setNotFound(true);
      setWallets(null);
      toast.error('Could not load Shop');
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  useEffect(() => {
    void loadShop();
  }, [loadShop]);

  useEffect(() => {
    if (!shopId || activeTab !== 'credit_transactions') return;
    const shopChanged = transferPrevShopIdRef.current !== shopId;
    if (shopChanged) transferPrevShopIdRef.current = shopId;
    const page = shopChanged ? 1 : transferTxPage;
    if (shopChanged && transferTxPage !== 1) {
      setTransferTxPage(1);
      return;
    }

    let cancelled = false;
    setTransferTxLoading(true);
    void (async () => {
      try {
        const res = await fetchAgentShopWalletTransactions({
          shop_id: shopId,
          page,
          limit: transferTxLimit,
          sort: 'desc',
        });
        if (cancelled) return;
        if (res.success && res.data) {
          setTransferTxs(res.data.items);
          setTransferTxTotalPages(res.data.pagination.totalPages);
          setTransferTxTotal(res.data.pagination.total);
        } else {
          setTransferTxs([]);
          setTransferTxTotalPages(0);
          setTransferTxTotal(0);
          if (res.message) toast.error(res.message);
        }
      } catch {
        if (cancelled) return;
        setTransferTxs([]);
        setTransferTxTotalPages(0);
        setTransferTxTotal(0);
        toast.error('Could not load transfer history');
      } finally {
        if (!cancelled) setTransferTxLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shopId, activeTab, transferTxPage, transferTxNonce, transferTxLimit]);

  useEffect(() => {
    if (!shopId || activeTab !== 'usd_transactions') return;
    const shopChanged = usdPrevShopIdRef.current !== shopId;
    if (shopChanged) usdPrevShopIdRef.current = shopId;
    const page = shopChanged ? 1 : usdTxPage;
    if (shopChanged && usdTxPage !== 1) {
      setUsdTxPage(1);
      return;
    }

    let cancelled = false;
    setUsdTxLoading(true);
    void (async () => {
      try {
        const offset = (page - 1) * usdTxLimit;
        const res = await fetchAgentShopOnlineBalanceLedger(shopId, {
          currency: 'USD',
          limit: usdTxLimit,
          offset,
        });
        if (cancelled) return;
        if (res.success && res.data) {
          setUsdTxs(res.data.rows ?? []);
          const total = Number(res.data.count ?? 0);
          setUsdTxTotal(total);
          setUsdTxTotalPages(total > 0 ? Math.ceil(total / usdTxLimit) : 0);
        } else {
          setUsdTxs([]);
          setUsdTxTotal(0);
          setUsdTxTotalPages(0);
        }
      } catch {
        if (cancelled) return;
        setUsdTxs([]);
        setUsdTxTotal(0);
        setUsdTxTotalPages(0);
      } finally {
        if (!cancelled) setUsdTxLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [shopId, activeTab, usdTxPage, usdTxNonce, usdTxLimit]);

  useEffect(() => {
    if (activeTab !== 'games' || !shopId) return;
    let cancelled = false;
    (async () => {
      setGamesLoading(true);
      try {
        const [mapRes, gpRes] = await Promise.all([fetchShopGameMappings(shopId), fetchPublicGameProviders()]);
        if (cancelled) return;
        const ids = mapRes.data?.game_ids ?? [];
        const pool = mapRes.data?.agent_effective_game_ids ?? [];
        const items =
          gpRes.data?.items?.filter((p) => p.status !== 'maintenance').map((p) => ({ id: p.id, name: p.name, code: p.code })) ??
          [];
        setAgentPoolIds(pool);
        setGameProviders(items);
        if (ids.length === 0) {
          setGameMode('all');
          setSelectedGameIds([]);
        } else {
          setGameMode('selected');
          setSelectedGameIds(ids);
        }
      } catch {
        if (!cancelled) toast.error('Could not load game configuration');
      } finally {
        if (!cancelled) setGamesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, shopId]);

  const ledgerDetail = usePortalLedgerDetailState({
    creditRows: transferTxs,
    usdRows: usdTxs,
    fetchCreditById: async (id) => {
      if (!shopId) throw new Error('No Shop');
      const res = await fetchAgentShopCreditTransactionById(shopId, id);
      if (!res.success || !res.data?.item) throw new Error(res.message || 'Not found');
      return res.data.item;
    },
    fetchUsdById: async (id) => {
      if (!shopId) throw new Error('No Shop');
      const res = await fetchAgentShopUsdLedgerEntryById(shopId, id);
      if (!res.success || !res.data?.item) throw new Error(res.message || 'Not found');
      return res.data.item;
    },
    onUnavailable: () =>
      toast.error('Related transaction is unavailable or outside your permission.'),
  });

  const visibleShopGames = useMemo(() => {
    if (agentPoolIds.length === 0) return gameProviders;
    const set = new Set(agentPoolIds);
    return gameProviders.filter((p) => set.has(p.id));
  }, [agentPoolIds, gameProviders]);

  const toggleShopGame = (gid: string) => {
    setSelectedGameIds((prev) => (prev.includes(gid) ? prev.filter((x) => x !== gid) : [...prev, gid]));
  };

  const saveShopGames = async () => {
    if (!shopId || savingGames) return;
    const payload = gameMode === 'all' ? [] : selectedGameIds;
    setSavingGames(true);
    try {
      const res = await putShopGameMappings(shopId, payload);
      if (!res.success) {
        toast.error((res as { message?: string }).message || 'Save failed');
        return;
      }
      toast.success('Game list saved');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Save failed';
      toast.error(msg);
    } finally {
      setSavingGames(false);
    }
  };

  useEffect(() => {
    if (!shop) return;
    const r = Number(shop.costRate);
    setCostPctStr(Number.isFinite(r) ? (r * 100).toFixed(2) : '0');
  }, [shop]);

  useEffect(() => {
    setNewShopPassword('');
    setConfirmShopPassword('');
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setActiveTab('overview');
  }, [shopId]);

  const applyPopupPermissionUpdate = async (nextValue: boolean) => {
    if (!shop) return;
    if (!dash?.can_manage_shop_popup_permission || !dash?.can_delegate_popups) {
      toast.error('You do not have permission to manage Shop popup settings');
      return;
    }
    setRowBusy(shop.id);
    try {
      const res = await updateShopPopupPermission(shop.id, nextValue);
      if (!res.success) {
        toast.error(res.message || 'Could not update popup setting');
        return;
      }
      toast.success('Popup permission updated');
      if (res.data) {
        setShop(res.data);
      } else {
        const shopRes = await fetchShop(shop.id);
        if (shopRes.success && shopRes.data) setShop(shopRes.data);
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not update popup setting';
      toast.error(msg);
    } finally {
      setRowBusy(null);
    }
  };

  const applyShopPasswordUpdate = async () => {
    if (!shop) return;
    if (!newShopPassword.trim()) {
      window.alert('Please enter a new password');
      return;
    }
    if (!confirmShopPassword.trim()) {
      window.alert('Please confirm the new password');
      return;
    }
    if (newShopPassword !== confirmShopPassword) {
      window.alert('Passwords do not match');
      return;
    }
    if (newShopPassword.length < MIN_SHOP_PASSWORD_LEN) {
      toast.error(`Password must be at least ${MIN_SHOP_PASSWORD_LEN} characters`);
      return;
    }
    setPasswordBusy(true);
    try {
      const res = await updateShopPassword(shop.id, newShopPassword);
      if (!res.success) {
        toast.error(res.message || 'Could not update password');
        return;
      }
      toast.success('Shop password updated');
      setNewShopPassword('');
      setConfirmShopPassword('');
      if (res.data) setShop(res.data);
      else await loadShop();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not update password';
      toast.error(msg);
    } finally {
      setPasswordBusy(false);
    }
  };

  const applyCostUpdate = async () => {
    if (!shop) return;
    const nextRate = percentInputToRate(costPctStr);
    const current = Number(shop.costRate ?? 0);
    const cur = Number.isFinite(current) ? current : 0;
    // Allow equality with agent cost rate; only reject when lower.
    if (nextRate + 1e-6 < minShopCostExclusive) {
      toast.error(
        `Shop cost must be at least your Agent cost (${(minShopCostExclusive * 100).toFixed(2)}%)`,
      );
      return;
    }
    if (Math.abs(nextRate - cur) < 1e-6) {
      toast.error('Cost rate is unchanged');
      return;
    }
    setCostBusy(true);
    try {
      const res = await updateShopCost(shop.id, nextRate);
      if (!res.success) {
        toast.error(res.message || 'Could not update cost');
        return;
      }
      toast.success('Cost updated');
      setCostModalOpen(false);
      if (res.data) setShop(res.data);
      else await loadShop();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not update cost';
      toast.error(msg);
    } finally {
      setCostBusy(false);
    }
  };

  const openNameModal = () => {
    if (!shop) return;
    setNameDraft(shop.name);
    setNameModalOpen(true);
  };

  const applyNameUpdate = async () => {
    if (!shop || shop.status === 'terminated') return;
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      toast.error('Display name is required');
      return;
    }
    if (trimmed === shop.name) {
      toast.error('Name is unchanged');
      return;
    }
    setNameBusy(true);
    try {
      const res = await updateShopName(shop.id, trimmed);
      if (!res.success) {
        toast.error(res.message || 'Could not update display name');
        return;
      }
      toast.success('Display name updated');
      setNameModalOpen(false);
      if (res.data) setShop(res.data);
      else await loadShop();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not update display name';
      toast.error(msg);
    } finally {
      setNameBusy(false);
    }
  };

  const runShopAction = async (
    fn: () => Promise<{ success: boolean; message?: string }>,
    successMessage?: string,
  ): Promise<boolean> => {
    if (!shop) return false;
    setRowBusy(shop.id);
    try {
      const res = await fn();
      if (!res.success) {
        toast.error(res.message || 'Action failed');
        return false;
      }
      if (successMessage) toast.success(successMessage);
      await loadShop();
      return true;
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Action failed';
      toast.error(msg);
      return false;
    } finally {
      setRowBusy(null);
    }
  };

  const openCostModal = () => {
    if (!shop) return;
    const r = Number(shop.costRate);
    setCostPctStr(Number.isFinite(r) ? (r * 100).toFixed(2) : '0');
    setCostModalOpen(true);
  };

  useEffect(() => {
    if (!costModalOpen && !nameModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (costModalOpen && !costBusy) setCostModalOpen(false);
      if (nameModalOpen && !nameBusy) setNameModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [costModalOpen, costBusy, nameModalOpen, nameBusy]);

  const copyShopId = async () => {
    if (!shop) return;
    try {
      await navigator.clipboard.writeText(shop.id);
      setCopiedShopId(true);
      window.setTimeout(() => setCopiedShopId(false), 2000);
    } catch {
      toast.error('Could not copy Shop ID');
    }
  };

  const statusBadge = (status: AgentShop['status']) => {
    if (status === 'active') return 'bg-[#F0FDF4] text-[#16A34A]';
    if (status === 'suspended') return 'bg-[#FEF3C7] text-[#D97706]';
    return 'bg-[#FEF2F2] text-[#DC2626]';
  };

  const statusLabel = (status: AgentShop['status']) => accountStatus(status).label;

  if (!shopId) {
    return (
      <AppLayout>
        <p className="text-gray-600">Invalid Shop.</p>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {loading ? (
        <PageLoadingIndicator variant="page" />
      ) : notFound || !shop ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-white p-12 text-center">
          <Store className="mx-auto h-10 w-10 text-gray-300" />
          <p className="mt-3 text-gray-600">Shop not found</p>
          <Link to="/shops" className="mt-4 inline-block text-sm font-semibold text-orange-700 hover:text-orange-900">
            Return to Shop list
          </Link>
        </div>
      ) : (
        <AgentPageLayout
          before={
            <Link
              to="/shops"
              className="inline-flex items-center gap-0.5 text-sm font-semibold text-[#C43D08] hover:text-[#A73305]"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
              Back to Shops
            </Link>
          }
          contentClassName="w-full min-w-0 pb-20 text-[15px] leading-[1.5] text-[#18181B]"
          contentStyle={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
        >
          <section className="rounded-[14px] border border-[#E4E4E7] bg-white p-5 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]">
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <div className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-xl bg-[#FEF0EB] text-lg font-bold text-[#E8551F]">
                  {(shop.name || 'S').slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <h1 className="truncate text-[20px] font-bold text-[#18181B]">{shop.name}</h1>
                  <p className="truncate text-[13px] text-[#71717A]">{shop.email}</p>
                  {shop.username?.trim() ? (
                    <p className="truncate text-[13px] text-[#71717A]">
                      <span className="text-[#A1A1AA]">Username </span>
                      <span className="font-medium text-[#18181B]">{shop.username.trim()}</span>
                    </p>
                  ) : null}
                  <span
                    className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadge(shop.status)}`}
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-current" />
                    {statusLabel(shop.status)}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-2 min-[400px]:flex-row min-[400px]:flex-wrap">
                <SellCreditsCashToShopTrigger
                  shopId={shop.id}
                  shopName={shop.name}
                  shopCostRate={shop.costRate}
                  disabled={shop.status === 'terminated' || shop.status === 'suspended'}
                  onSuccess={() => {
                    void loadShop();
                    setTransferTxPage(1);
                    setTransferTxNonce((n) => n + 1);
                  }}
                  className="w-full !justify-center !border-[#E8551F] !bg-[#E8551F] !px-[14px] !py-[9px] !text-[13px] !font-semibold !text-white hover:!bg-[#C43D08] min-[400px]:w-auto"
                />
                <WithdrawCreditsFromShopTrigger
                  shopId={shop.id}
                  shopName={shop.name}
                  shopStatus={shop.status}
                  shopCostRate={shop.costRate}
                  sourceCreditBalance={wallets?.credit_wallet.available_balance}
                  onSuccess={() => {
                    void loadShop();
                    setTransferTxPage(1);
                    setTransferTxNonce((n) => n + 1);
                  }}
                  className="w-full !justify-center min-[400px]:w-auto"
                />
                <button
                  type="button"
                  disabled={!!rowBusy || shop.status === 'terminated' || nameBusy}
                  onClick={openNameModal}
                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-[8px] border border-[#D4D4D8] bg-white px-[14px] py-[9px] text-[13px] font-semibold text-[#18181B] hover:bg-[#F2F1EF] disabled:opacity-50 min-[400px]:w-auto"
                >
                  <Pencil className="h-3.5 w-3.5" aria-hidden />
                  Edit display name
                </button>
                <button
                  type="button"
                  disabled={!!rowBusy || shop.status === 'terminated' || costBusy}
                  onClick={openCostModal}
                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-[8px] border border-[#D4D4D8] bg-white px-[14px] py-[9px] text-[13px] font-semibold text-[#18181B] hover:bg-[#F2F1EF] disabled:opacity-50 min-[400px]:w-auto"
                >
                  <Percent className="h-3.5 w-3.5" aria-hidden />
                  Update cost rate
                </button>
                <button
                  type="button"
                  disabled={!!rowBusy || shop.status !== 'active'}
                  onClick={() => {
                    if (!window.confirm('Suspend this Shop?')) return;
                    void runShopAction(() => suspendShop(shop.id), 'Shop suspended');
                  }}
                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-[8px] border border-[#D4D4D8] bg-white px-[14px] py-[9px] text-[13px] font-semibold text-[#18181B] hover:bg-[#F2F1EF] disabled:opacity-50 min-[400px]:w-auto"
                >
                  <Pause className="h-3.5 w-3.5" />
                  Suspend
                </button>
              </div>
            </div>
          </section>

          <div className="mt-4 rounded-[14px] border border-[#E4E4E7] bg-white p-1.5 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]">
            <div className="flex gap-1">
              {[
                { key: 'overview' as const, label: 'Overview' },
                { key: 'games' as const, label: 'Game' },
                { key: 'security' as const, label: 'Security' },
                { key: 'credit_transactions' as const, label: PAGE.creditTransactions },
                { key: 'usd_transactions' as const, label: 'USD wallet transactions' },
              ].map((tab) => {
                const active = tab.key === activeTab;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex-1 rounded-[8px] px-3 py-2 text-[13px] font-semibold transition ${
                      active
                        ? 'bg-[#E8551F] text-white shadow-[0_2px_8px_rgba(232,85,31,0.35)]'
                        : 'text-[#71717A] hover:bg-[#F2F1EF] hover:text-[#18181B]'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {activeTab === 'overview' && (
            <div className="mt-4 space-y-4">
              <section className="rounded-[14px] border border-[#E4E4E7] bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]">
                <h2 className="text-sm font-semibold">Contact</h2>
                <div className="mt-2">
                  <div className="flex items-start gap-3 border-b border-[#E4E4E7] py-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] bg-[#F2F1EF] text-[#71717A]">
                      <Mail className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-[#71717A]">Email</p>
                      <p className="truncate font-medium text-[#18181B]">{shop.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 border-b border-[#E4E4E7] py-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] bg-[#F2F1EF] text-[#71717A]">
                      <User className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-[#71717A]">Username</p>
                      <p className="truncate font-medium text-[#18181B]">{shop.username?.trim() || '—'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 py-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] bg-[#F2F1EF] text-[#71717A]">
                      <Phone className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-[#71717A]">Phone</p>
                      <p className="truncate font-medium text-[#18181B]">{shop.phone || '—'}</p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="rounded-[14px] border border-[#E4E4E7] bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]">
                <h2 className="text-sm font-semibold">Account</h2>

                <div className="mt-4">
                  <p className="text-xs text-[#71717A]">Cost rate</p>
                  <p className="mt-1 text-lg font-bold tabular-nums text-[#18181B]">{formatCostRate(shop.costRate)}</p>

                  <div className="mt-3">
                    <div className="h-1.5 rounded-full bg-[#E4E4E7]">
                      <div
                        className="h-1.5 rounded-full transition-all"
                        style={{
                          width: `${savedCostPercent}%`,
                          backgroundColor: savedBarColor,
                        }}
                      />
                    </div>
                    <div className="mt-1 flex justify-between text-[11px] text-[#A1A1AA]">
                      <span>0%</span>
                      <span>Minimum {Math.round(minShopCostPercent)}%</span>
                      <span>100%</span>
                    </div>
                  </div>

                  <p className="mt-2 text-xs text-[#71717A]">
                    Must be at least {Math.round(minShopCostPercent)}% (Agent cost) · Cash owed = credits × cost
                  </p>
                </div>

                <div className="mt-5">
                  <p className="text-xs text-[#71717A]">Shop ID</p>
                  <div className="mt-1 flex items-center gap-2">
                    <div className="min-w-0 flex-1 truncate rounded-[8px] bg-[#F2F1EF] px-3 py-2 font-mono text-xs text-[#71717A]">
                      {shop.id}
                    </div>
                    <button
                      type="button"
                      onClick={() => void copyShopId()}
                      className={`inline-flex items-center gap-1 rounded-[8px] border px-3 py-2 text-xs font-semibold ${
                        copiedShopId
                          ? 'border-[#86EFAC] text-[#16A34A]'
                          : 'border-[#D4D4D8] text-[#18181B] hover:bg-[#F2F1EF]'
                      }`}
                    >
                      <Copy className="h-3.5 w-3.5" />
                      {copiedShopId ? 'Copied ✓' : 'Copy'}
                    </button>
                  </div>
                </div>

                {wallets ? (
                  <div className="mt-5">
                    <p className="text-xs text-[#71717A]">Wallets</p>
                    <div className="mt-2 grid gap-3">
                      <div className="rounded-[12px] border border-[#E4E4E7] bg-[#FAFAFA] p-3">
                        <p className="text-xs font-semibold text-[#18181B]">Credit wallet</p>
                        <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <p className="text-[#A1A1AA]">Balance</p>
                            <p className="font-bold tabular-nums text-[#18181B]">
                              {wallets.credit_wallet.balance.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div>
                            <p className="text-[#A1A1AA]">Reserved</p>
                            <p className="font-bold tabular-nums text-[#18181B]">
                              {wallets.credit_wallet.reserved_balance.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div>
                            <p className="text-[#A1A1AA]">Available</p>
                            <p className="font-bold tabular-nums text-[#18181B]">
                              {wallets.credit_wallet.available_balance.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>
                        <p className="mt-2 text-[11px] text-[#A1A1AA]">
                          {wallets.credit_wallet.currency} · {wallets.credit_wallet.status}
                        </p>
                      </div>

                      <div className="rounded-[12px] border border-[#E4E4E7] bg-[#FAFAFA] p-3">
                        <p className="text-xs font-semibold text-[#18181B]">USD wallet</p>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <p className="text-[#A1A1AA]">Net</p>
                            <p className="font-bold tabular-nums text-[#18181B]">
                              {wallets.usd_wallet.balance_net.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div>
                            <p className="text-[#A1A1AA]">Gross</p>
                            <p className="font-bold tabular-nums text-[#18181B]">
                              {wallets.usd_wallet.balance_gross.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <p className="text-[#A1A1AA]">Reserved net</p>
                            <p className="font-bold tabular-nums text-[#18181B]">
                              {wallets.usd_wallet.reserved_net.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div>
                            <p className="text-[#A1A1AA]">Reserved gross</p>
                            <p className="font-bold tabular-nums text-[#18181B]">
                              {wallets.usd_wallet.reserved_gross.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>
                        <p className="mt-2 text-[11px] text-[#A1A1AA]">
                          {wallets.usd_wallet.currency} · {wallets.usd_wallet.status}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}

                {dash?.can_delegate_popups && dash?.can_manage_shop_popup_permission ? (
                  <div className="mt-5">
                    <p className="text-xs text-[#71717A]">Popup / broadcast permission</p>
                    <div className="mt-2 flex items-start gap-3">
                      <label className="inline-flex cursor-pointer items-start gap-2 text-sm">
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 rounded border-[#D4D4D8] text-[#E8551F] focus:ring-[#E8551F]"
                          checked={!!shop.canCreatePlayerPopup}
                          disabled={!!rowBusy || shop.status === 'terminated'}
                          onChange={(e) => void applyPopupPermissionUpdate(e.target.checked)}
                        />
                        <span>
                          <span className="block text-sm text-[#18181B]">Send popups to Players</span>
                          <span className="mt-0.5 block text-xs text-[#71717A]">
                            Allow the Shop to send direct notifications to Players
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>
                ) : null}
              </section>
            </div>
          )}

          {activeTab === 'games' && (
            <section className="mt-4 rounded-[14px] border border-[#E4E4E7] bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]">
              <div className="flex items-center gap-2">
                <Gamepad2 className="h-4 w-4 text-[#E8551F]" />
                <h2 className="text-sm font-semibold">Games for this Shop</h2>
              </div>
              <p className="mt-1 text-xs text-[#71717A]">
                {agentPoolIds.length === 0
                  ? 'Agent: all games. You can limit the list below.'
                  : `Agent allows ${agentPoolIds.length} games — select only from this list.`}
              </p>
              {gamesLoading ? (
                <div className="py-6">
                  <PageLoadingIndicator variant="inline" label="Loading…" />
                </div>
              ) : (
                <div className="mt-4 space-y-4">
                  <div className="flex flex-wrap gap-4 text-sm text-[#18181B]">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="shop-game-mode"
                        checked={gameMode === 'all'}
                        onChange={() => setGameMode('all')}
                        disabled={shop.status === 'terminated'}
                      />
                      All games in the Agent pool
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="shop-game-mode"
                        checked={gameMode === 'selected'}
                        onChange={() => setGameMode('selected')}
                        disabled={shop.status === 'terminated'}
                      />
                      Selected games only
                    </label>
                  </div>
                  {gameMode === 'selected' ? (
                    <div className="max-h-64 overflow-y-auto rounded-[8px] border border-[#E4E4E7] p-3 space-y-2">
                      {visibleShopGames.length === 0 ? (
                        <p className="text-sm text-[#71717A]">No games available.</p>
                      ) : (
                        visibleShopGames.map((p) => (
                          <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedGameIds.includes(p.id)}
                              onChange={() => toggleShopGame(p.id)}
                              disabled={shop.status === 'terminated'}
                            />
                            <span>{p.name}</span>
                            <span className="text-xs text-[#A1A1AA]">({p.code})</span>
                          </label>
                        ))
                      )}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => void saveShopGames()}
                    disabled={savingGames || shop.status === 'terminated'}
                    className="rounded-[8px] bg-[#E8551F] px-4 py-2 text-sm font-semibold text-white hover:bg-[#C43D08] disabled:opacity-50"
                  >
                    {savingGames ? 'Saving…' : 'Save'}
                  </button>
                </div>
              )}
            </section>
          )}

          {activeTab === 'security' && (
            <section className="mt-4 rounded-[14px] border border-[#E4E4E7] bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]">
              <h2 className="text-sm font-semibold">Change sign-in password</h2>
              <p className="mt-1 text-sm text-[#71717A]">
                The Shop signs in with this email and password. When you update it, all current sessions will be signed out.
              </p>

              <div className="mt-4 space-y-3">
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={newShopPassword}
                    onChange={(e) => setNewShopPassword(e.target.value)}
                    disabled={shop.status === 'terminated' || passwordBusy}
                    placeholder="New password"
                    className="w-full rounded-[8px] border border-[#D4D4D8] px-3 py-2 pr-10 text-[15px] text-[#18181B] outline-none focus:border-[#E8551F] disabled:bg-[#F2F1EF]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#18181B]"
                    aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={confirmShopPassword}
                    onChange={(e) => setConfirmShopPassword(e.target.value)}
                    disabled={shop.status === 'terminated' || passwordBusy}
                    placeholder="Confirm password"
                    className="w-full rounded-[8px] border border-[#D4D4D8] px-3 py-2 pr-10 text-[15px] text-[#18181B] outline-none focus:border-[#E8551F] disabled:bg-[#F2F1EF]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#18181B]"
                    aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <button
                  type="button"
                  disabled={shop.status === 'terminated' || passwordBusy}
                  onClick={() => void applyShopPasswordUpdate()}
                  className="w-full rounded-[8px] bg-[#18181B] px-4 py-2.5 text-[15px] font-bold text-white hover:bg-black disabled:opacity-50"
                >
                  {passwordBusy ? 'Updating…' : 'Update password'}
                </button>
              </div>
            </section>
          )}

          {activeTab === 'credit_transactions' && (
            <section className="mt-4 rounded-[14px] border border-[#E4E4E7] bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]">
              <div className="mb-3">
                <h2 className="text-sm font-semibold">{PAGE.creditTransactions}</h2>
                <p className="mt-0.5 text-xs text-[#71717A]">Credit wallet entries for this Shop.</p>
              </div>
              {transferTxLoading ? (
                <div className="py-6">
                  <PageLoadingIndicator variant="inline" label="Loading transactions…" />
                </div>
              ) : transferTxs.length === 0 ? (
                <p className="py-8 text-center text-sm text-[#71717A]">No transfers yet.</p>
              ) : (
                <div>
                  {transferTxs.map((tx, idx) => {
                    const p = getPresentation(tx);
                    const isDebit = p.signedAmount < 0;
                    return (
                      <article
                        key={tx.id}
                        className={`flex items-start gap-3 py-3 ${
                          idx !== transferTxs.length - 1 ? 'border-b border-[#E4E4E7]' : ''
                        }`}
                      >
                        <div
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] ${
                            isDebit ? 'bg-[#FEF2F2] text-[#DC2626]' : 'bg-[#F0FDF4] text-[#16A34A]'
                          }`}
                        >
                          {isDebit ? <ArrowDownRight className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-[15px] font-medium text-[#18181B]">{txCreatedAt(tx, timezone)}</p>
                            <div className="flex items-center gap-2">
                              <p
                                className={`text-[15px] font-bold tabular-nums ${
                                  isDebit ? 'text-[#DC2626]' : 'text-[#16A34A]'
                                }`}
                              >
                                {formatSigned(p.signedAmount)}
                              </p>
                              <ViewTransactionButton onClick={() => ledgerDetail.openCredit(tx)} size="sm" />
                            </div>
                          </div>
                          <p className="mt-0.5 text-sm font-medium text-[#18181B]">{p.title}</p>
                          <p className="mt-0.5 truncate text-xs text-[#A1A1AA]">
                            {p.counterparty ? `Counterparty: ${p.counterparty}` : '—'} · Ref: {p.shortRef ?? '—'}
                          </p>
                        </div>
                      </article>
                    );
                  })}

                  {transferTxTotalPages > 1 && (
                    <div className="mt-4 flex flex-col items-center justify-between gap-3 border-t border-[#E4E4E7] pt-3 sm:flex-row">
                      <p className="text-sm text-[#71717A]">
                        Page {transferTxPage} of {transferTxTotalPages} · {transferTxTotal} total
                      </p>
                      <NumberedPagination
                        currentPage={transferTxPage}
                        totalPages={transferTxTotalPages}
                        onPageChange={setTransferTxPage}
                        disabled={transferTxLoading}
                        variant="portal"
                        className="justify-center sm:justify-end"
                      />
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {activeTab === 'usd_transactions' && (
            <section className="mt-4 rounded-[14px] border border-[#E4E4E7] bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]">
              <div className="mb-3">
                <h2 className="text-sm font-semibold">USD wallet transactions</h2>
                <p className="mt-0.5 text-xs text-[#71717A]">USD wallet entries for this Shop.</p>
              </div>
              {usdTxLoading ? (
                <div className="py-6">
                  <PageLoadingIndicator variant="inline" label="Loading USD wallet transactions…" />
                </div>
              ) : usdTxs.length === 0 ? (
                <p className="py-8 text-center text-sm text-[#71717A]">No USD wallet transactions yet.</p>
              ) : (
                <div>
                  {usdTxs.map((r, idx) => {
                    const signed = signedNetAmount(r);
                    const isDebit = signed < 0;
                    const typeText = PORTAL_TERMS.usd.typeDisplay(usdLedgerTypeLabel(r));
                    const provider = (r as any)?.provider ? String((r as any)?.provider) : '—';
                    const statusText = (r as any)?.status ? ledgerStatus(String((r as any)?.status)).label : '—';
                    return (
                      <article
                        key={r.id}
                        className={`flex items-start gap-3 py-3 ${
                          idx !== usdTxs.length - 1 ? 'border-b border-[#E4E4E7]' : ''
                        }`}
                      >
                        <div
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] ${
                            isDebit ? 'bg-[#FEF2F2] text-[#DC2626]' : 'bg-[#F0FDF4] text-[#16A34A]'
                          }`}
                        >
                          {isDebit ? <ArrowDownRight className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-[15px] font-medium text-[#18181B]">{usdTxCreatedAt(r)}</p>
                            <div className="flex items-center gap-2">
                              <p
                                className={`text-[15px] font-bold tabular-nums ${
                                  isDebit ? 'text-[#DC2626]' : 'text-[#16A34A]'
                                }`}
                              >
                                {signed.toFixed(2)}
                              </p>
                              <ViewTransactionButton
                                onClick={() => {
                                  const id = r.id != null ? String(r.id) : '';
                                  if (id) void ledgerDetail.openUsdById(id);
                                }}
                                size="sm"
                              />
                            </div>
                          </div>
                          <p className="mt-0.5 text-sm font-medium text-[#18181B]">{typeText}</p>
                          <p className="mt-0.5 truncate text-xs text-[#A1A1AA]">
                            {provider} · {statusText}
                          </p>
                        </div>
                      </article>
                    );
                  })}

                  {usdTxTotalPages > 1 && (
                    <div className="mt-4 flex flex-col items-center justify-between gap-3 border-t border-[#E4E4E7] pt-3 sm:flex-row">
                      <p className="text-sm text-[#71717A]">
                        Page {usdTxPage} of {usdTxTotalPages} · {usdTxTotal} total
                      </p>
                      <NumberedPagination
                        currentPage={usdTxPage}
                        totalPages={usdTxTotalPages}
                        onPageChange={setUsdTxPage}
                        disabled={usdTxLoading}
                        variant="portal"
                        className="justify-center sm:justify-end"
                      />
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {nameModalOpen && shop && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <button
                type="button"
                className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
                aria-label="Close dialog"
                onClick={() => {
                  if (!nameBusy) setNameModalOpen(false);
                }}
              />
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="shop-name-modal-title"
                className="relative z-10 w-full max-w-md rounded-[14px] border border-[#E4E4E7] bg-white p-6 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 id="shop-name-modal-title" className="text-lg font-semibold text-[#18181B]">
                  Edit display name
                </h2>
                <p className="mt-1 text-sm text-[#71717A]">{shop.email}</p>
                <div className="mt-5">
                  <label htmlFor="shop-display-name-input" className="text-xs font-medium text-[#71717A]">
                    Display name
                  </label>
                  <input
                    id="shop-display-name-input"
                    type="text"
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    disabled={shop.status === 'terminated' || nameBusy}
                    className="mt-2 w-full rounded-[8px] border-[1.5px] border-[#D4D4D8] px-3 py-2 text-[15px] font-semibold text-[#18181B] outline-none focus:border-[#E8551F] disabled:bg-[#F2F1EF]"
                    autoComplete="organization"
                  />
                </div>
                <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    disabled={nameBusy}
                    onClick={() => setNameModalOpen(false)}
                    className="rounded-[8px] border border-[#D4D4D8] bg-white px-4 py-2 text-sm font-semibold text-[#18181B] hover:bg-[#F2F1EF] disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={shop.status === 'terminated' || nameBusy || nameDraft.trim() === ''}
                    onClick={() => void applyNameUpdate()}
                    className="rounded-[8px] bg-[#E8551F] px-4 py-2 text-sm font-bold text-white hover:bg-[#C43D08] disabled:opacity-50"
                  >
                    {nameBusy ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {costModalOpen && shop && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <button
                type="button"
                className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
                aria-label="Close dialog"
                onClick={() => {
                  if (!costBusy) setCostModalOpen(false);
                }}
              />
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="shop-cost-modal-title"
                className="relative z-10 w-full max-w-md rounded-[14px] border border-[#E4E4E7] bg-white p-6 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 id="shop-cost-modal-title" className="text-lg font-semibold text-[#18181B]">
                  Update cost rate
                </h2>
                <p className="mt-1 truncate text-sm text-[#71717A]">{shop.name}</p>

                <div className="mt-5">
                  <label htmlFor="shop-cost-rate-input" className="text-xs font-medium text-[#71717A]">
                    Cost rate (%)
                  </label>
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      id="shop-cost-rate-input"
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min={minShopCostPercent}
                      value={costPctStr}
                      onChange={(e) => setCostPctStr(e.target.value)}
                      disabled={shop.status === 'terminated' || costBusy}
                      className="w-28 rounded-[8px] border-[1.5px] border-[#D4D4D8] px-3 py-2 text-center font-bold text-[#18181B] outline-none focus:border-[#E8551F] disabled:bg-[#F2F1EF]"
                    />
                    <span className="text-sm text-[#71717A]">%</span>
                  </div>

                  <div className="mt-4">
                    <div className="h-1.5 rounded-full bg-[#E4E4E7]">
                      <div
                        className="h-1.5 rounded-full transition-all"
                        style={{
                          width: `${normalizedCostPercent}%`,
                          backgroundColor: costProgressColor,
                        }}
                      />
                    </div>
                    <div className="mt-1 flex justify-between text-[11px] text-[#A1A1AA]">
                      <span>0%</span>
                      <span>Minimum {Math.round(minShopCostPercent)}%</span>
                      <span>100%</span>
                    </div>
                  </div>

                  <p className="mt-3 text-xs text-[#71717A]">
                    Must be at least {Math.round(minShopCostPercent)}% (Agent cost) · Cash owed = credits × cost
                  </p>
                </div>

                <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    disabled={costBusy}
                    onClick={() => setCostModalOpen(false)}
                    className="rounded-[8px] border border-[#D4D4D8] bg-white px-4 py-2 text-sm font-semibold text-[#18181B] hover:bg-[#F2F1EF] disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={shop.status === 'terminated' || costBusy}
                    onClick={() => void applyCostUpdate()}
                    className="rounded-[8px] bg-[#E8551F] px-4 py-2 text-sm font-bold text-white hover:bg-[#C43D08] disabled:opacity-50"
                  >
                    {costBusy ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </AgentPageLayout>
      )}

      <CreditTransactionDetailModal
        entry={ledgerDetail.creditDetail}
        onClose={ledgerDetail.closeCredit}
        accountLabel="Shop credit wallet"
        onOpenUsdSource={ledgerDetail.handleOpenUsdSource}
        relatedLoading={ledgerDetail.relatedLoading}
        resolveUsdConversion={ledgerDetail.resolveUsdConversion}
      />
      <UsdWalletTransactionDetailModal
        row={ledgerDetail.usdDetail}
        onClose={ledgerDetail.closeUsd}
        onOpenCreditSource={ledgerDetail.handleOpenCreditSource}
        onOpenUsdSource={ledgerDetail.handleOpenUsdSource}
        relatedLoading={ledgerDetail.relatedLoading}
      />
    </AppLayout>
  );
}
